import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Bars3Icon, 
  BellIcon, 
  ChevronDownIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import NotificationDropdown from '../notifications/NotificationDropdown';
import { notificationsAPI } from '../../services/api';

const Navbar = ({ onOpenSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [installEvent, setInstallEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const isSupplier = user?.accountType === 'supplier';
  const displayName = isSupplier
    ? (user?.businessName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Supplier')
    : `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const initials = (displayName || user?.email || 'U').trim().charAt(0).toUpperCase();
  const profileHref = isSupplier ? '/supplier/settings' : '/profile';

  useEffect(() => {
    let timer = null;
    const loadUnread = async () => {
      try {
        const { data } = await notificationsAPI.list({ unreadOnly: true, limit: 1 });
        const count = Array.isArray(data.notifications) ? data.notifications.length : 0;
        setUnread(count);
      } catch (_) {}
    };
    loadUnread();
    timer = setInterval(loadUnread, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // PWA install prompt handling
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstallEvent(null);
      setIsInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const isStandalone = (typeof window !== 'undefined') && (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (navigator.standalone === true);
  const canInstall = !!installEvent && !isInstalled && !isStandalone;

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Left side - Logo and navigation */}
          <div className="flex items-center">
            {/* Mobile menu button */}
            <button
              type="button"
              className="mr-3 inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 md:hidden"
              onClick={() => onOpenSidebar && onOpenSidebar()}
              aria-label="Open sidebar"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div className="flex-shrink-0 flex items-center">
              <div className="flex items-center">
                {/* Professional Logo */}
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 via-green-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C10.9 2 10 2.9 10 4C10 4.7 10.4 5.4 11 5.7V7C11 7.6 11.4 8 12 8S13 7.6 13 7V5.7C13.6 5.4 14 4.7 14 4C14 2.9 13.1 2 12 2ZM12 9C8.7 9 6 11.7 6 15V16C6 16.6 6.4 17 7 17H8V19C8 20.1 8.9 21 10 21H14C15.1 21 16 20.1 16 19V17H17C17.6 17 18 16.6 18 16V15C18 11.7 15.3 9 12 9ZM14 19H10V17H14V19ZM16 15H8C8 12.8 9.8 11 12 11S16 12.8 16 15Z"/>
                      <circle cx="9" cy="13" r="1" fill="currentColor"/>
                      <circle cx="15" cy="13" r="1" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
                
                {/* Company Name */}
                <div className="ml-3">
                  <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-green-600 to-yellow-600 bg-clip-text text-transparent">
                    Omabra
                  </span>
                  <div className="text-xs text-gray-500 font-medium tracking-wide">
                    POULTRY ASSISTANT
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - User menu and notifications */}
          <div className="flex items-center space-x-4">
            {/* Install PWA CTA */}
            {canInstall && (
              <button
                className="hidden sm:inline-flex items-center px-3 py-2 rounded-md btn-secondary"
                onClick={async () => {
                  try {
                    await installEvent.prompt();
                    await installEvent.userChoice;
                  } catch (_) {}
                  setInstallEvent(null);
                }}
              >
                Install App
              </button>
            )}
            {/* Notifications */}
            <div className="relative">
              <button
                className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md relative"
                onClick={() => setIsNotifOpen(v => !v)}
                aria-label="Open notifications"
              >
                <BellIcon className="h-6 w-6" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium leading-none text-white bg-red-500 rounded-full">
                    {unread}
                  </span>
                )}
              </button>
              <div className="absolute right-0">
                <NotificationDropdown
                  isOpen={isNotifOpen}
                  onClose={() => setIsNotifOpen(false)}
                />
              </div>
            </div>

            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {user?.avatarUrl ? (
                  <img 
                    src={user.avatarUrl} 
                    alt={displayName || 'User'} 
                    className="h-8 w-8 rounded-full object-cover border border-gray-200"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-semibold flex items-center justify-center">
                    {initials}
                  </div>
                )}
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                  </p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <Link
                    to={profileHref}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <Cog6ToothIcon className="h-4 w-4 mr-3" />
                    {isSupplier ? 'Business Settings' : 'Profile Settings'}
                  </Link>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
